import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_CONFIG, fetchAPI } from '@/services/api';
import { initFacebookPixel, trackFacebookPageView } from '@/utils/facebookPixel';

export function PaymentConfirmation() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    const scriptId = 'ghl-calendar-embed';
    if (document.getElementById(scriptId)) return;
    const script = document.createElement('script');
    script.id = scriptId;
    script.src = 'https://link.msgsndr.com/js/form_embed.js';
    script.async = true;
    document.body.appendChild(script);
  }, []);

  useEffect(() => {
    initFacebookPixel();
    trackFacebookPageView();
  }, []);

  async function handleSetPassword(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    if (!email.trim()) {
      setError('Email is required.');
      return;
    }
    if (!password || password.length < 6) {
      setError('Password must be at least 6 characters.');
      return;
    }
    if (password !== confirm) {
      setError('Passwords do not match.');
      return;
    }

    setSaving(true);
    try {
      const tokenRes = await fetchAPI<any>(`${API_CONFIG.USERS_API}`, {
        method: 'POST',
        body: JSON.stringify({
          action: 'create_password_token',
          email: email.trim(),
        }),
      });
      if (!tokenRes?.success || !tokenRes?.token) {
        setError(tokenRes?.error || 'Unable to create password token.');
        return;
      }

      const setRes = await fetchAPI<any>(`${API_CONFIG.USERS_API}`, {
        method: 'POST',
        body: JSON.stringify({
          action: 'set_password_with_token',
          email: email.trim(),
          token: tokenRes.token,
          newPassword: password,
        }),
      });
      if (!setRes?.success) {
        setError(setRes?.error || 'Failed to set password.');
        return;
      }

      setSuccess('Password set. You can log in now.');
      setPassword('');
      setConfirm('');
    } catch (err: any) {
      setError(err?.message || 'Failed to set password.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="payment-confirm-page min-h-screen bg-dark-bg p-6 text-white">
      <div className="max-w-5xl mx-auto space-y-6">
        <div className="payment-surface bg-gray-900/70 border border-gray-700 rounded-lg p-6">
          <h1 className="text-2xl font-semibold mb-2">Payment Approved</h1>
          <p className="text-gray-300">
            Your payment was successful. A receipt will be emailed to you shortly.
          </p>
          <div className="mt-6 flex flex-wrap gap-3">
            <button
              onClick={() => navigate('/lead-search')}
              className="px-4 py-2 rounded bg-green-600 hover:bg-green-500 text-white font-semibold"
            >
              Get Started Now
            </button>
            <a
              href="#training"
              className="px-4 py-2 rounded bg-blue-600 hover:bg-blue-500 text-white font-semibold"
            >
              Schedule Training
            </a>
          </div>
        </div>

        <div className="payment-surface bg-gray-900/70 border border-gray-700 rounded-lg p-6">
          <h2 className="text-xl font-semibold mb-2">Create Your Password</h2>
          <p className="text-gray-300 text-sm mb-4">
            Set your password to access the platform immediately.
          </p>
          <form onSubmit={handleSetPassword} className="grid gap-4 max-w-lg">
            <label className="grid gap-2 text-sm text-gray-300">
              Email
              <input
                type="email"
                className="w-full bg-gray-900 border border-gray-700 rounded px-3 py-2 text-white"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com"
                required
              />
            </label>
            <label className="grid gap-2 text-sm text-gray-300">
              Password
              <input
                type="password"
                className="w-full bg-gray-900 border border-gray-700 rounded px-3 py-2 text-white"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Min 6 characters"
                required
              />
            </label>
            <label className="grid gap-2 text-sm text-gray-300">
              Confirm Password
              <input
                type="password"
                className="w-full bg-gray-900 border border-gray-700 rounded px-3 py-2 text-white"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                placeholder="Repeat password"
                required
              />
            </label>

            {error && (
              <div className="bg-red-900/40 border border-red-700 rounded px-3 py-2 text-sm text-red-200">
                {error}
              </div>
            )}
            {success && (
              <div className="bg-green-900/40 border border-green-700 rounded px-3 py-2 text-sm text-green-200">
                {success}
              </div>
            )}

            <button
              type="submit"
              disabled={saving}
              className="px-4 py-2 rounded bg-emerald-600 hover:bg-emerald-500 text-white font-semibold disabled:opacity-60"
            >
              {saving ? 'Saving…' : 'Set Password'}
            </button>
          </form>
        </div>

        <div id="training" className="payment-surface bg-gray-900/70 border border-gray-700 rounded-lg p-6">
          <h2 className="text-xl font-semibold mb-4">Schedule Training</h2>
          <div className="w-full">
            <iframe
              src="https://api.leadconnectorhq.com/widget/booking/igTo5NGMRez7r7ZAhtbq"
              style={{ width: '100%', border: 'none', overflow: 'hidden' }}
              scrolling="no"
              id="igTo5NGMRez7r7ZAhtbq_1770919821708"
              title="Schedule Training"
            />
          </div>
        </div>
      </div>
    </div>
  );
}
